/**
 * Lightweight Toast system
 * SOLID: Single Responsibility - renders and manages transient toast notifications
 * DRY: Reusable across the app via useToast()
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import Icon, { type IconName } from "../AppIcon";
import { registerGlobalToast } from "./toastBridge";

export type ToastType = "success" | "error" | "info" | "warning";

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  showToast: (type: ToastType, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_STYLES: Record<ToastType, { container: string; icon: IconName }> = {
  success: { container: "bg-green-600 text-white", icon: "CheckCircle" },
  error: { container: "bg-red-600 text-white", icon: "AlertCircle" },
  warning: { container: "bg-orange-500 text-white", icon: "AlertTriangle" },
  info: { container: "bg-blue-600 text-white", icon: "Info" },
};

const TOAST_DURATION_MS = 4000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => dismiss(id), TOAST_DURATION_MS);
    },
    [dismiss]
  );

  useEffect(() => {
    registerGlobalToast(showToast);
    return () => registerGlobalToast(null);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed top-4 right-4 z-100 flex flex-col gap-2 w-full max-w-sm"
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${TOAST_STYLES[toast.type].container}`}
          >
            <Icon name={TOAST_STYLES[toast.type].icon} size={18} className="shrink-0" />
            <span className="text-sm font-medium flex-1">{toast.message}</span>
            <button
              onClick={() => dismiss(toast.id)}
              className="ml-1 opacity-70 hover:opacity-100 transition-opacity shrink-0"
              aria-label="Dismiss notification"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
};
